import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Plus, Search, Users, LayoutDashboard, Pencil, Trash2 } from "lucide-react";
import type { Task, TaskColumn, TaskTeam } from "@/features/tasks/types/task";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useTasks, type BoardKey } from "@/features/tasks/hooks/useTasks";
import { getTeams, deleteTeam as deleteTeamApi } from "@/features/tasks/services/taskTeamService";
import {
  deleteColumn as deleteColumnApi,
} from "@/features/tasks/services/taskColumnService";
import { deleteTask as deleteTaskApi, getTask } from "@/features/tasks/services/taskService";
import { KanbanBoard } from "@/features/tasks/components/KanbanBoard";
import { TaskDetailPanel } from "@/features/tasks/components/TaskDetailPanel";
import { TaskFormModal } from "@/features/tasks/components/TaskFormModal";
import { ColumnFormModal } from "@/features/tasks/components/ColumnFormModal";
import { TeamFormModal } from "@/features/tasks/components/TeamFormModal";
import { RemindersBar } from "@/features/tasks/components/RemindersBar";
import { Button } from "@/shared/components/ui/Button";
import { Spinner } from "@/shared/components/ui/Spinner";
import { Toast } from "@/shared/components/feedback/Toast";
import { ProfileAvatar } from "@/shared/components/ui/Avatar";

interface ToastState {
  type: "success" | "error" | "info";
  message: string;
}

export default function TasksPage() {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const location = useLocation();
  const { columns, tasks, loading, error, fetchBoard, moveTask, refreshTasks, toggleComplete } = useTasks();

  // Active board: 'personal' | team_id | 'teams' (management tab)
  const [activeBoard, setActiveBoard] = useState<BoardKey | "teams">("personal");

  const [teams, setTeams]           = useState<TaskTeam[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);

  // Filter state (board tab only)
  const [searchQuery, setSearchQuery]   = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  // Modals
  const [selectedTask, setSelectedTask]   = useState<Task | null>(null);
  const [detailOpen, setDetailOpen]       = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask]     = useState<Task | null>(null);
  const [defaultColumnId, setDefaultColumnId] = useState<string | undefined>();
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [editingColumn, setEditingColumn] = useState<TaskColumn | null>(null);
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [editingTeam, setEditingTeam]     = useState<TaskTeam | null>(null);

  // Toast
  const [toast, setToast] = useState<ToastState | null>(null);

  const showToast = (type: ToastState["type"], message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const loadTeams = async () => {
    setTeamsLoading(true);
    try {
      const data = await getTeams();
      setTeams(data);
    } catch {
      // silently fail
    } finally {
      setTeamsLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchBoard("personal");
    loadTeams();
  }, []);

  // Open task panel when navigated from a notification
  useEffect(() => {
    const openTaskId = (location.state as { openTaskId?: string } | null)?.openTaskId;
    if (!openTaskId) return;
    getTask(openTaskId)
      .then((task) => {
        if (task.team_id) setActiveBoard(task.team_id as BoardKey);
        handleSelectTask(task);
      })
      .catch(console.error);
  }, []);

  // Refetch board when active tab changes (not management tab)
  useEffect(() => {
    if (activeBoard !== "teams") {
      fetchBoard(activeBoard);
    }
  }, [activeBoard]);

  // Teams the current user is a member of (officers/admins see all)
  const myTeams = useMemo(() => {
    const uid = String(userData?.id ?? "");
    return teams.filter((team) => team.members.some((m) => String(m.user_id) === uid));
  }, [teams, userData]);

  // Distinct categories for the active board
  const categories = useMemo(() => {
    const cats = new Set<string>();
    tasks.forEach((t) => { if (t.category) cats.add(t.category); });
    return Array.from(cats).sort();
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    return tasks.filter((task) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (
          !task.title.toLowerCase().includes(q) &&
          !task.description?.toLowerCase().includes(q) &&
          !task.category?.toLowerCase().includes(q)
        ) return false;
      }
      if (categoryFilter && task.category !== categoryFilter) return false;
      return true;
    });
  }, [tasks, searchQuery, categoryFilter]);

  // Tasks relevant to the current user for the reminders bar:
  // assigned to them, or belonging to a team they're a member of
  const reminderEligibleTasks = useMemo(() => {
    const uid = String(userData?.id ?? "");
    return tasks.filter((task) => {
      if (task.assignees.some((a) => String(a.user_id) === uid)) return true;
      if (task.team_id && myTeams.some((t) => t.id === task.team_id)) return true;
      return false;
    });
  }, [tasks, userData, myTeams]);

  // The team for the current board (if on a team board)
  const activeBoardTeam = useMemo(
    () => (activeBoard !== "personal" && activeBoard !== "teams"
      ? myTeams.find((t) => t.id === activeBoard) ?? null
      : null),
    [activeBoard, myTeams],
  );

  // ─── Handlers ─────────────────────────────────────────────────────────────────

  const handleAddTask = (columnId: string) => {
    setEditingTask(null);
    setDefaultColumnId(columnId);
    setTaskModalOpen(true);
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setDefaultColumnId(undefined);
    setTaskModalOpen(true);
    setDetailOpen(false);
  };

  const handleSelectTask = (task: Task) => {
    setSelectedTask(task);
    setDetailOpen(true);
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      await deleteTaskApi(taskId);
      if (activeBoard !== "teams") await refreshTasks(activeBoard);
      showToast("success", t("tasks.delete_success", { defaultValue: "Task deleted" }));
    } catch {
      showToast("error", t("tasks.error.delete_failed"));
    }
  };

  const handleMoveTask = async (taskId: string, columnId: string, order: number) => {
    try {
      await moveTask(taskId, columnId, order);
    } catch {
      showToast("error", t("tasks.error.move_failed"));
    }
  };

  const handleCompleteTask = async (task: Task) => {
    await toggleComplete(task.id);
  };

  const handleAddColumn = () => {
    setEditingColumn(null);
    setColumnModalOpen(true);
  };

  const handleEditColumn = (column: TaskColumn) => {
    setEditingColumn(column);
    setColumnModalOpen(true);
  };

  const handleDeleteColumn = async (columnId: string) => {
    if (!window.confirm(t("tasks.column.delete_confirm"))) return;
    try {
      const ownerType = activeBoard === "personal" ? "user" : "team";
      const ownerId   = activeBoard === "personal" ? undefined : activeBoard;
      await deleteColumnApi(columnId, ownerType, ownerId);
      if (activeBoard !== "teams") await fetchBoard(activeBoard);
      showToast("success", "Column deleted");
    } catch {
      showToast("error", "Failed to delete column");
    }
  };

  const handleDeleteTeam = async (teamId: string) => {
    if (!window.confirm(t("tasks.team.delete_confirm"))) return;
    try {
      await deleteTeamApi(teamId);
      await loadTeams();
      // If we were viewing this team's board, go back to personal
      if (activeBoard === teamId) setActiveBoard("personal");
      showToast("success", "Team deleted");
    } catch {
      showToast("error", "Failed to delete team");
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  const isBoard = activeBoard !== "teams";

  return (
    <div className="flex flex-col min-h-full">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-[100]">
          <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />
        </div>
      )}

      {/* Page header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("tasks.page_title")}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t("tasks.page_subtitle")}</p>
        </div>
        {isBoard && (
          <Button
            onClick={() => {
              setEditingTask(null);
              setDefaultColumnId(columns[0]?.id);
              setTaskModalOpen(true);
            }}
            className="flex items-center gap-1.5 shrink-0"
          >
            <Plus className="w-4 h-4" />
            {t("tasks.new_task")}
          </Button>
        )}
      </div>

      {/* Reminders bar (only on board tabs) */}
      {isBoard && reminderEligibleTasks.length > 0 && (
        <div className="mb-4">
          <RemindersBar tasks={reminderEligibleTasks} columns={columns} onSelectTask={handleSelectTask} />
        </div>
      )}

      {/* Tab bar */}
      <div className="flex border-b border-gray-200 mb-4 gap-1 overflow-x-auto">
        {/* My Board */}
        <button
          onClick={() => setActiveBoard("personal")}
          className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors rounded-t-lg whitespace-nowrap ${
            activeBoard === "personal"
              ? "text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50"
              : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
          }`}
        >
          <LayoutDashboard className="w-4 h-4" />
          {t("tasks.tabs.my_board", { defaultValue: "My Board" })}
        </button>

        {/* One tab per team the user belongs to */}
        {myTeams.map((team) => (
          <button
            key={team.id}
            onClick={() => setActiveBoard(team.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors rounded-t-lg whitespace-nowrap ${
              activeBoard === team.id
                ? "text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            <Users className="w-4 h-4" />
            {team.name}
          </button>
        ))}

        {/* Teams management tab */}
        <button
          onClick={() => setActiveBoard("teams")}
          className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors rounded-t-lg whitespace-nowrap ml-auto ${
            activeBoard === "teams"
              ? "text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50"
              : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
          }`}
        >
          <Users className="w-4 h-4" />
          {t("tasks.tabs.teams", { defaultValue: "Teams" })}
        </button>
      </div>

      {/* ── Board tab (personal or team) ─────────────────────────────────────── */}
      {isBoard && (
        <>
          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="w-4 h-4 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("tasks.search_placeholder")}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {categories.length > 0 && (
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="py-2 pl-3 pr-8 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">{t("tasks.all_categories")}</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            )}
          </div>

          {/* Board content */}
          {loading ? (
            <div className="flex justify-center py-16"><Spinner /></div>
          ) : error ? (
            <div className="text-center py-16 text-red-500">
              <p>{t("tasks.error.load_failed")}</p>
              <Button variant="secondary" size="sm" onClick={() => fetchBoard(activeBoard)} className="mt-3">
                Retry
              </Button>
            </div>
          ) : columns.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-16 text-gray-400">
              <p className="text-base">{t("tasks.no_columns")}</p>
              <Button variant="secondary" size="sm" onClick={handleAddColumn} className="flex items-center gap-1.5">
                <Plus className="w-4 h-4" />
                {t("tasks.new_column")}
              </Button>
            </div>
          ) : (
            <KanbanBoard
              columns={columns}
              tasks={filteredTasks}
              onMoveTask={handleMoveTask}
              onAddTask={handleAddTask}
              onSelectTask={handleSelectTask}
              onEditTask={handleEditTask}
              onAddColumn={handleAddColumn}
              onEditColumn={handleEditColumn}
              onDeleteColumn={handleDeleteColumn}
              onCompleteTask={handleCompleteTask}
            />
          )}
        </>
      )}

      {/* ── Teams management tab ─────────────────────────────────────────────── */}
      {activeBoard === "teams" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900">{t("tasks.team.title")}</h2>
            <Button
              size="sm"
              onClick={() => { setEditingTeam(null); setTeamModalOpen(true); }}
              className="flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" />
              {t("tasks.new_team")}
            </Button>
          </div>

          {teamsLoading ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : myTeams.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-12 text-gray-400">
              <Users className="w-10 h-10" />
              <p>{t("tasks.team.no_teams")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {myTeams.map((team) => (
                <div
                  key={team.id}
                  className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">{team.name}</h3>
                      {team.description && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{team.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => { setEditingTeam(team); setTeamModalOpen(true); }}
                        className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeleteTeam(team.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Members preview */}
                  <div className="flex items-center gap-2 mt-3">
                    <div className="flex -space-x-1.5">
                      {team.members.slice(0, 4).map((m) => (
                        <div key={m.user_id} className="ring-2 ring-white rounded-full">
                          <ProfileAvatar
                            picId={m.profile_pic_id}
                            firstName={m.first_name}
                            lastName={m.last_name}
                            size="sm"
                          />
                        </div>
                      ))}
                    </div>
                    <span className="text-xs text-gray-500">
                      {t("tasks.team.member_count_other", { count: team.member_count })}
                    </span>
                  </div>

                  {/* Open board button */}
                  <button
                    onClick={() => setActiveBoard(team.id)}
                    className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
                  >
                    <LayoutDashboard className="w-3.5 h-3.5" />
                    {t("tasks.team.open_board", { defaultValue: "Open Board" })}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Task Detail Panel */}
      <TaskDetailPanel
        task={selectedTask}
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        onEdit={handleEditTask}
        onDelete={handleDeleteTask}
        onRefresh={() => { if (activeBoard !== "teams") refreshTasks(activeBoard); }}
        onError={(err) => showToast("error", err?.response?.data?.message ?? t("tasks.error.save_failed", { defaultValue: "Failed to save" }))}
        columns={columns}
        teams={teams}
      />

      {/* Task Form Modal */}
      <TaskFormModal
        isOpen={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        onSaved={async () => {
          if (activeBoard !== "teams") await refreshTasks(activeBoard);
          showToast("success", editingTask ? t("tasks.form.save") : t("tasks.form.create"));
        }}
        onError={(err) => showToast("error", err?.response?.data?.message ?? t("tasks.error.save_failed", { defaultValue: "Failed to save task" }))}
        task={editingTask}
        defaultColumnId={defaultColumnId}
        columns={columns}
        teams={teams}
        lockedTeamId={activeBoardTeam?.id}
      />

      {/* Column Form Modal */}
      <ColumnFormModal
        isOpen={columnModalOpen}
        onClose={() => setColumnModalOpen(false)}
        onSaved={async () => {
          if (activeBoard !== "teams") await fetchBoard(activeBoard);
          showToast("success", "Column saved");
        }}
        onError={(err) => showToast("error", err?.response?.data?.message ?? t("tasks.error.save_failed", { defaultValue: "Failed to save column" }))}
        column={editingColumn}
        nextOrder={columns.length}
        ownerType={activeBoard === "personal" ? "user" : "team"}
        ownerId={activeBoard === "personal" ? undefined : activeBoard}
      />

      {/* Team Form Modal */}
      <TeamFormModal
        isOpen={teamModalOpen}
        onClose={() => setTeamModalOpen(false)}
        onSaved={async () => {
          await loadTeams();
          showToast("success", "Team saved");
        }}
        onError={(err) => showToast("error", err?.response?.data?.message ?? t("tasks.error.save_failed", { defaultValue: "Failed to save team" }))}
        team={editingTeam}
      />
    </div>
  );
}
